import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  UjpApiError,
  ujpAPI,
  type UjpEstimatePayload,
  type UjpEstimateResult,
} from '@/services/api/ujp';

/**
 * useUjpEstimate — the live, SERVER-computed estimate behind the wizard.
 *
 * The browser never runs the formula (assessment D5): every change to the
 * inputs is debounced 300 ms and sent to `POST /v1/ujp/estimate`; the previous
 * request is aborted and a monotonic sequence number drops any answer that
 * arrives out of order, so an older total can never overwrite a newer one.
 *
 * State machine (PRD req. 6 / §14.3):
 *   idle     — payload incomplete (`null`), nothing to compute; `result` kept
 *   stale    — inputs changed after the last answer, request not yet fired
 *              ("Estimasi belum diperbarui")
 *   pending  — request in flight (amount dims, never blank)
 *   ok       — `result` is the answer for the CURRENT payload
 *   error    — last request failed; `result` is the last good answer, kept so
 *              the strip still shows a number, and Lanjut/Ajukan block on it
 *
 * `refresh()` re-fires immediately for the current payload ("Coba lagi" and
 * the Review step's final recheck).
 */

export type UjpEstimateStatus = 'idle' | 'stale' | 'pending' | 'ok' | 'error';

export interface UseUjpEstimateResult {
  status: UjpEstimateStatus;
  /** Last good answer (may be for an older payload when stale/error). */
  result: UjpEstimateResult | null;
  /** True when `result` answers the current payload exactly. */
  fresh: boolean;
  error: string | null;
  refresh: () => void;
}

export const ESTIMATE_DEBOUNCE_MS = 300;

export function useUjpEstimate(
  payload: UjpEstimatePayload | null,
  { enabled = true }: { enabled?: boolean } = {}
): UseUjpEstimateResult {
  const [status, setStatus] = useState<UjpEstimateStatus>('idle');
  const [result, setResult] = useState<UjpEstimateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The payload key the current `result` answers — freshness is a comparison,
  // not a flag, so a payload edited back to a known value is fresh again.
  const [resultKey, setResultKey] = useState<string | null>(null);

  const seqRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  const key = useMemo(
    () => (payload && enabled ? JSON.stringify(payload) : null),
    [payload, enabled]
  );

  const fire = useCallback(() => {
    const current = payloadRef.current;
    if (!current) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const seq = ++seqRef.current;
    const firedKey = JSON.stringify(current);
    setStatus('pending');
    setError(null);
    ujpAPI
      .estimate(current, controller.signal)
      .then((res) => {
        if (seq !== seqRef.current) return; // superseded — drop
        setResult(res);
        setResultKey(firedKey);
        setStatus('ok');
      })
      .catch((err: unknown) => {
        if (seq !== seqRef.current) return;
        if (err instanceof UjpApiError && err.aborted) return;
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Estimasi gagal');
      });
  }, []);

  useEffect(() => {
    if (key === null) {
      controllerRef.current?.abort();
      seqRef.current++;
      setStatus('idle');
      return;
    }
    if (key === resultKey) {
      // Back on a payload we already answered — no request, no stale chip.
      setStatus((s) => (s === 'error' ? s : 'ok'));
      return;
    }
    setStatus('stale');
    const timer = setTimeout(fire, ESTIMATE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // resultKey is deliberately not a dependency: an answer landing must not
    // re-run the debounce for the same key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, fire]);

  // Abort on unmount so a closing modal never leaves a request answering into
  // unmounted state.
  useEffect(() => () => controllerRef.current?.abort(), []);

  const refresh = useCallback(() => {
    if (!payloadRef.current) return;
    fire();
  }, [fire]);

  return {
    status,
    result,
    fresh: key !== null && key === resultKey && status === 'ok',
    error,
    refresh,
  };
}
